<script>
import {modalState, hideModal} from '@app/store/modalStore.svelte.js';
import {authState} from '@app/store/authStore.svelte.js';
import { projectState } from '@app/store/projectStore.svelte.js';
import { fade } from 'svelte/transition';
import * as m from "@app/paraglide/messages.js";
import { toast } from '@zerodevx/svelte-toast';
import { functions } from '@app/configs/firebase.js';
import { httpsCallable } from 'firebase/functions';
import { applyTeamCommit } from '@app/lib/applyTeamCommit.js';
import { establishHistoryBaseline, hasUnsavedChanges } from '@app/store/historyStore.svelte.js';
import Swal from 'sweetalert2';
import { SWAL_BASE, SWAL_CLASSES } from '@app/lib/swalClasses.js';

/* Firebase */
import db from '@app/configs/firestore.js';
import { collection, doc as fsDoc, query, orderBy, getDocs, deleteDoc } from 'firebase/firestore';

let snapshots = $state([]);
let selectedSnapshot = $state();
let restoring = $state(false);

let teamId = authState.team.id;
let dbSnapshotRef = collection(db, "teams", teamId, "projects", projectState.teamId, "snapshots");
let dbCommitRef = collection(db, "teams", teamId, "projects", projectState.teamId, "commits");
let gettingSnapshots = loadSnapshotHistory();

function formatCreatedOn(value) {
    if (!value) {
        return "";
    }
    if (typeof value.toDate === "function") {
        return value.toDate();
    }
    return value;
}

function loadSnapshotHistory(){
    snapshots = [];
    selectedSnapshot = undefined;
    return getDocs(query(dbSnapshotRef, orderBy("createdOn", "desc"))).then(snapshotRes =>{
        snapshotRes.forEach(doc =>{
            snapshots = [...snapshots, doc.data()];
        });
    });
}

function canRestore() {
    return selectedSnapshot && selectedSnapshot.status === "complete" && selectedSnapshot.commit && !restoring;
}

function canDelete() {
    if (!selectedSnapshot || restoring) {
        return false;
    }
    if (selectedSnapshot.status === "in progress") {
        return true;
    }
    return snapshots[0] && selectedSnapshot.id === snapshots[0].id;
}

async function restoreCommit(){
    if (!canRestore()) {
        return;
    }

    const result = await Swal.fire({
        ...SWAL_BASE,
        customClass: SWAL_CLASSES.confirmCancel,
        title: m.snapshot_history_restore_confirm_title(),
        text: hasUnsavedChanges()
            ? m.snapshot_history_restore_confirm_unsaved()
            : m.snapshot_history_restore_confirm_text(),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: m.snapshot_history_restore_button(),
        cancelButtonText: m.snapshot_history_close_button(),
        allowOutsideClick: false,
    });

    if (!result.isConfirmed) {
        return;
    }

    restoring = true;
    try {
        const restoreRes = await httpsCallable(functions, "v8RestoreTeamProject")({
            projectTeamId: projectState.teamId,
            commitId: selectedSnapshot.commit,
        });
        const commitUrl = Array.isArray(restoreRes.data) ? restoreRes.data[0] : restoreRes.data;
        const commitFileRes = await fetch(commitUrl);
        if (!commitFileRes.ok) {
            throw new Error(`Failed to download restored commit (${commitFileRes.status})`);
        }
        const commitFileJson = await commitFileRes.json();
        await applyTeamCommit(commitFileJson, { replaceAll: true, applyIdentity: false });
        establishHistoryBaseline({ name: "project restore" });
        toast.push(m.snapshot_history_restore_success(), { classes: ["toast-success"] });
        hideModal();
    } catch (err) {
        console.error(err);
        toast.push(m.snapshot_history_restore_error({ message: err.message }), { classes: ["toast-danger"] });
    } finally {
        restoring = false;
        gettingSnapshots = loadSnapshotHistory();
    }
}

async function deleteCommit(){
    if (!canDelete()) {
        return;
    }

    const isInProgress = selectedSnapshot.status === "in progress";
    const result = await Swal.fire({
        ...SWAL_BASE,
        customClass: SWAL_CLASSES.confirmCancel,
        title: isInProgress ? m.snapshot_history_cancel_confirm_title() : m.snapshot_history_delete_confirm_title(),
        text: isInProgress ? m.snapshot_history_cancel_confirm_text() : m.snapshot_history_delete_confirm_text(),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: isInProgress ? m.snapshot_history_cancel_sync_button() : m.snapshot_history_delete_button(),
        cancelButtonText: m.snapshot_history_close_button(),
        allowOutsideClick: false,
    });

    if (!result.isConfirmed) {
        return;
    }

    try {
        if (isInProgress) {
            await httpsCallable(functions, "v8FailProjectSync")({
                id: selectedSnapshot.id,
                projectTeamId: projectState.teamId,
            });
            toast.push(m.snapshot_history_cancel_success(), { classes: ["toast-success"] });
        } else {
            await deleteDoc(fsDoc(dbSnapshotRef, selectedSnapshot.id));
            if (selectedSnapshot.commit) {
                await deleteDoc(fsDoc(dbCommitRef, selectedSnapshot.commit));
            }
            toast.push(m.snapshot_history_delete_success(), { classes: ["toast-success"] });
        }

        gettingSnapshots = loadSnapshotHistory();
    } catch(err){
        console.error(err);
        toast.push(m.snapshot_history_delete_error({ message: err.message }), { classes: ["toast-danger"] });
    }    
}
</script>
  
  <div 
    transition:fade={{duration: 100}} 
    class="modal fade {modalState.selected === 'snapshotHistory' ? 'show d-block' : ''}" 
    role="dialog" 
    aria-labelledby="snapshotHistoryTitle"
    aria-describedby="snapshotHistoryDescription"
    tabindex="-1" 
    id="SnapshotHistoryModal"
>
    <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title" id="snapshotHistoryTitle">{m.snapshot_history_title()}</h4>
          <button 
            type="button" 
            class="btn-close" 
            aria-label={m.snapshot_history_close_aria_label()} 
            id="closeSnapshotHistory"
            onclick={hideModal}
            disabled={restoring}
          ></button>
        </div>
        <div class="modal-body">
          <p id="snapshotHistoryDescription" class="visually-hidden">
            {m.snapshot_history_description()}
          </p>
          <div class="row">
            <div class="col-6">
                <button 
                  type="button" 
                  class="btn btn-light float-end mb-1" 
                  id="refreshSnapshots"
                  aria-label={m.snapshot_history_refresh_aria_label()} 
                  onclick={() => { gettingSnapshots = loadSnapshotHistory(); }}
                  disabled={restoring}
                ><i class="bi bi-arrow-clockwise" aria-hidden="true"></i></button>
                <select 
                  class="form-select form-select-sm" 
                  size="15" 
                  bind:value={selectedSnapshot}
                  aria-label={m.snapshot_history_select_aria_label()}
                  id="snapshotSelect"
                  disabled={restoring}
                >
                    {#each snapshots as snapshot (snapshot.id)}
                        <option 
                          class="text-wrap {snapshot.status === 'in progress' ? 'text-danger' : ''}" 
                          value={snapshot}
                          aria-label={m.snapshot_history_commit_option_label({ user: snapshot.user, message: snapshot.message || m.snapshot_history_no_message() })}
                        >{snapshot.user} | {snapshot.message || m.snapshot_history_no_message()}</option>
                    {/each}
                </select>
            </div>
            <div class="col">
                <div class="d-flex justify-content-end gap-2 mb-1">
                    <button 
                      type="button" 
                      class="btn btn-primary" 
                      disabled={!canRestore()}
                      onclick={restoreCommit}
                      id="restoreCommit"
                      aria-label={m.snapshot_history_restore_aria_label()}
                    ><i class="bi bi-arrow-counterclockwise" aria-hidden="true"></i> {m.snapshot_history_restore_button()}</button>
                    <button 
                      type="button" 
                      class="btn btn-danger" 
                      disabled={!canDelete()}
                      onclick={deleteCommit}
                      id="deleteCommit"
                      aria-label={m.snapshot_history_delete_aria_label()}
                    ><i class="bi bi-trash" aria-hidden="true"></i> {selectedSnapshot?.status === "in progress" ? m.snapshot_history_cancel_sync_button() : m.snapshot_history_delete_button()}</button>
                </div>
                <table class="table table-light table-striped" aria-label={m.snapshot_history_details_aria_label()}>
                    <tbody>
                      <tr>
                        <th scope="row">{m.snapshot_history_id_label()}</th>
                        <td>{selectedSnapshot ? selectedSnapshot.commit  : ""}</td>
                      </tr>      
                      <tr>
                        <th scope="row">{m.snapshot_history_created_on_label()}</th>
                        <td>{selectedSnapshot ? formatCreatedOn(selectedSnapshot.createdOn)  : ""}</td>
                      </tr>      
                      <tr>
                        <th scope="row">{m.snapshot_history_completed_label()}</th>
                        <td>
                          <i 
                            class="bi bi-{selectedSnapshot && selectedSnapshot.status === 'complete' ? 'check-lg' : 'slash-circle'}"
                            aria-label={selectedSnapshot && selectedSnapshot.status === 'complete' ? m.snapshot_history_completed_status() : m.snapshot_history_not_completed_status()}
                          ></i>
                        </td>
                      </tr>      
                      <tr>
                        <th scope="row">{m.snapshot_history_username_label()}</th>
                        <td>{selectedSnapshot ? selectedSnapshot.user  : ""}</td>
                      </tr> 
                      <tr>
                        <th scope="row">{m.snapshot_history_message_label()}</th>
                        <td>{selectedSnapshot && selectedSnapshot.message ? selectedSnapshot.message  : ""}</td>
                      </tr>                   
                    </tbody>
                  </table>
            </div>
          </div>
        </div>
        <div class="modal-footer">
            <button 
              type="button" 
              class="btn btn-light" 
              onclick={hideModal}
              id="closeModalBtn"
              disabled={restoring}
              aria-label={m.snapshot_history_close_aria_label()}
            >{m.snapshot_history_close_button()}</button>
        </div>
      </div>
    </div>
  </div>
